import { useState } from "react";
import { X, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "../../lib/content";

export const Gallery = () => {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = active === "All" ? GALLERY : GALLERY.filter((g) => g.category === active);

  const openAt = (item) => setLightbox(GALLERY.indexOf(item));
  const step = (dir) => setLightbox((i) => (i + dir + GALLERY.length) % GALLERY.length);

  return (
    <section id="work" data-testid="gallery-section" className="relative py-24 md:py-32 bg-[#111111] border-t border-gold/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-cursive text-gold text-2xl md:text-3xl">Our Work</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mt-1">Project Gallery</h2>
          <div className="gold-divider w-40 mx-auto mt-6" />
          <p className="mt-6 text-gray-400">Real jobs, real results. Browse the trees we've tamed and the properties we've protected throughout the Everett area.</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                active === c ? "bg-gold text-black border-gold" : "border-gold/30 text-gray-300 hover:border-gold hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {filtered.map((g, i) => (
            <button
              key={g.url}
              data-testid={`gallery-item-${i}`}
              onClick={() => openAt(g)}
              className="group relative mb-5 block w-full overflow-hidden rounded-xl border border-gold/20 hover:border-gold/60 transition-all duration-500 break-inside-avoid"
            >
              <img src={g.url} alt={g.title} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 p-5 text-left">
                <span className="text-[10px] uppercase tracking-widest text-gold">{g.category}</span>
                <h3 className="font-serif text-lg text-white mt-0.5">{g.title}</h3>
              </div>
            </button>
          ))}
        </div>

        <div className="text-center mt-14">
          <a
            href="#estimate"
            data-testid="gallery-cta"
            className="inline-flex items-center gap-2 bg-gold text-black font-bold px-8 py-4 rounded-sm hover:bg-gold-light transition-colors uppercase tracking-widest text-sm"
          >
            Start Your Project <ArrowRight size={16} />
          </a>
        </div>
      </div>

      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button data-testid="lightbox-close" className="absolute top-6 right-6 text-white hover:text-gold" onClick={() => setLightbox(null)}>
            <X size={32} />
          </button>
          <button data-testid="lightbox-prev" className="absolute left-4 md:left-10 text-white hover:text-gold" onClick={(e) => { e.stopPropagation(); step(-1); }}>
            <ChevronLeft size={44} />
          </button>
          <div className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={GALLERY[lightbox].url} alt={GALLERY[lightbox].title} className="max-h-[78vh] w-auto mx-auto rounded-lg border border-gold/30" />
            <div className="text-center mt-4">
              <span className="text-xs uppercase tracking-widest text-gold">{GALLERY[lightbox].category}</span>
              <h3 className="font-serif text-2xl text-white">{GALLERY[lightbox].title}</h3>
            </div>
          </div>
          <button data-testid="lightbox-next" className="absolute right-4 md:right-10 text-white hover:text-gold" onClick={(e) => { e.stopPropagation(); step(1); }}>
            <ChevronRight size={44} />
          </button>
        </div>
      )}
    </section>
  );
};
